import PropTypes from 'prop-types';
import React from 'react';
import styled, { css } from 'styled-components';
import { surfaceM, primaryM, primaryDisabledMTransparent, primaryDisabledM, primaryHighlightM, grayLightM } from 'web-ui-kit/colors';
import { buttonClicked } from 'web-ui-kit/buttons/events';
import { ButtonLabelStyles, ButtonLabelSmallStyles } from 'web-ui-kit/typography';

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7;
/**
 * Use secondary button to provide users a non-primary action alternative.
 */


function SecondaryButton(_ref) {
  var _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$dispatch = _ref.dispatch,
      dispatch = _ref$dispatch === void 0 ? function () {} : _ref$dispatch,
      _ref$documentId = _ref.documentId,
      documentId = _ref$documentId === void 0 ? '' : _ref$documentId,
      _ref$fluid = _ref.fluid,
      fluid = _ref$fluid === void 0 ? false : _ref$fluid,
      _ref$Icon = _ref.Icon,
      Icon = _ref$Icon === void 0 ? null : _ref$Icon,
      _ref$id = _ref.id,
      id = _ref$id === void 0 ? 'secondary' : _ref$id,
      _ref$labelText = _ref.labelText,
      labelText = _ref$labelText === void 0 ? '' : _ref$labelText,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      _ref$sizeQuery = _ref.sizeQuery,
      sizeQuery = _ref$sizeQuery === void 0 ? '(min-width: 600px)' : _ref$sizeQuery,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'submit' : _ref$type,
      _ref$variant = _ref.variant,
      variant = _ref$variant === void 0 ? 'default' : _ref$variant;

  var handleClick = function handleClick(jsEvent) {
    dispatch(buttonClicked({
      disabled: disabled,
      id: id,
      jsEvent: jsEvent,
      labelText: labelText
    }));
  };

  var Button = styles.Button;
  return (
    /*#__PURE__*/
    React.createElement(Button, {
      className: className,
      fluid: fluid,
      hasIconAndLabel: Icon && labelText,
      id: documentId,
      isDisabled: disabled,
      onClick: handleClick,
      px: labelText ? '0.875rem' : '0.4375rem',
      size: size,
      sizeQuery: sizeQuery,
      variant: variant,
      type: type
    }, Icon, labelText)
  );
}

SecondaryButton.propTypes = {
  /** Allows extending styles with styled(SecondaryButton) constructor */
  className: PropTypes.string,

  /** Whether or not the button is currently disabled */
  disabled: PropTypes.bool,

  /** For reporting events up to parent */
  dispatch: PropTypes.func,

  /** id value that is visible from the document */
  documentId: PropTypes.string,

  /** Whether or not the button should have fluid width */
  fluid: PropTypes.bool,

  /**
   * An optional button icon.
   * The icon should be sized to fit well in the button.
   */
  Icon: PropTypes.element,

  /**
   * An optional button id string that can be used
   * to help identify the origin of button click events.
   * This string is not user visible.
   */
  id: PropTypes.string,

  /** The button label text */
  labelText: PropTypes.string,

  /**
   * Button size options.
   * If responsive is chosen then button starts at 'small'
   * size and switches to 'medium' size at 'sizeQuery'.
   * One of:
   */
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'responsive']),

  /**
   * If size is 'responsive', mobile first query where button
   * switches from 'small' to 'medium' size.
   */
  sizeQuery: PropTypes.string,

  /** The button type. One of: */
  variant: PropTypes.oneOf(['default', 'transparent'])
};
/**
 * Styles.
 */

var styles = {
  Button: styled.button(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    ", ";\n\n    ", ";\n\n    display: flex;\n    align-items: center;\n    justify-content: center;\n\n    appearance: none;\n    background-color: ", ";\n    border: 1px solid ", ";\n    border-radius: 4px;\n    color: ", ";\n    outline: none;\n    overflow: hidden;\n    padding: 0.4375rem ", ";\n    transition: all 300ms,\n                visibility 0s;\n    white-space: nowrap;\n\n    ", "\n\n    ", "\n\n    ", "\n\n    ", "\n\n    ", "\n\n    ", "\n  "])), ButtonLabelStyles, function (p) {
    return p.size === 'xsmall' && ButtonLabelSmallStyles;
  }, surfaceM, primaryM, primaryM, function (p) {
    return p.px;
  }, function (p) {
    return p.hasIconAndLabel && css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      svg {\n        margin-right: 0.5rem;\n      }\n    "])));
  }, function (p) {
    return p.fluid && "\n      width: 100%;\n    ";
  }, function (p) {
    return p.size === 'medium' && css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n      padding: 0.625rem ", ";\n    "])), function (p) {
      return p.px;
    });
  }, function (p) {
    return p.size === 'responsive' && css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      @media ", " {\n        padding: 0.625rem ", ";\n      }\n    "])), p.sizeQuery, function (p) {
      return p.px;
    });
  }, function (p) {
    return p.isDisabled ? css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n      background-color: ", ";\n      border-color: ", ";\n      color: ", ";\n      pointer-events: none;\n    "])), primaryDisabledMTransparent, primaryDisabledM, primaryDisabledM) : css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n      cursor: pointer;\n      @media (hover: hover) {\n        &:hover {\n          border-color: ", ";\n          color: ", ";\n        }\n      }\n    "])), primaryHighlightM, primaryHighlightM);
  }, function (p) {
    return p.variant === 'transparent' && css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n      background-color: transparent;\n      border-color: ", ";\n      color: ", ";\n\n      @media (hover: hover) {\n        &:hover {\n          border-color: ", ";\n          color: ", ";\n        }\n      }\n    "])), surfaceM, surfaceM, grayLightM, grayLightM);
  })
};
export default SecondaryButton;